import { cloneDeep } from 'lodash';
import { nanoid } from 'nanoid';
import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import undoable from 'redux-undo';
import { arrayMove } from '@dnd-kit/sortable';
import {
  type DraggableId,
  type FrameIndex,
  type Frames,
  type OverlayItem,
  type OverlayItemWithImage,
  OverlayItemWithVideo,
  type OverlayLayer,
  type PackagingTimelineState,
  type SegmentItem,
  type Source
} from '@/redux/slices/packagingSlice.types';
import { AssetType } from './slice_types';
import { RootState } from '../store';
import { setActiveTemplate } from './packagingSlice';
import { identifyDefaultSegmentDuration } from '@/components/Packaging/utils';
import { isAssignedOverlay } from '@/components/Packaging/typeGuards';

const initialState: PackagingTimelineState = {
  segments: [],
  overlays: [[]]
};

interface TimelineActionMeta {
  excludeFromHistory: boolean;
}

type AppendSegmentPayload = Omit<SegmentItem, 'start' | 'end'>;
type AppendSegmentPayloadAction = PayloadAction<AppendSegmentPayload>; // this action allows passing SegmentItem without the `start` and `end` properties because they will be calculated and added inside the reducer

interface InsertSegmentAtIndexPayload {
  segment: Omit<SegmentItem, 'start' | 'end'>; // this action allows passing SegmentItem without the `start` and `end` properties because they will be calculated and added inside the reducer
  index: number;
}
type InsertSegmentAtIndexPayloadAction = PayloadAction<InsertSegmentAtIndexPayload, string, TimelineActionMeta | undefined>;

type InsertOverlayPayloadAction = PayloadAction<{
  layerIndex: number;
  overlay: OverlayItem;
}>;

type MoveOverlayPayloadAction = PayloadAction<{
  currentLayerIndex: number;
  newLayerIndex: number;
  overlayId: DraggableId;
  start: FrameIndex;
  end: FrameIndex;
}>;

type ResizeOverlayPayloadAction = PayloadAction<{
  overlayId: DraggableId;
  layerIndex: number;
  end: FrameIndex;
}>;

interface MoveSegmentPayload {
  currentIndex: number;
  newIndex: number;
}
type MoveSegmentPayloadAction = PayloadAction<MoveSegmentPayload, string, TimelineActionMeta | undefined>;

type SetSegmentSourcePayloadAction = PayloadAction<{
  segmentId: DraggableId;
  source: Source;
  duration: Frames;
  subtitle: string;
}>;

type SetSegmentPreviewUrlPayload = Record<DraggableId, Pick<Source, 'previewUrl' | 'previewLoadingError'>>;
type SetSegmentPreviewUrlPayloadAction = PayloadAction<SetSegmentPreviewUrlPayload>;

type SetOverlaySourcePayloadAction = PayloadAction<{
  overlayId: DraggableId;
  sourceType: Extract<AssetType, 'image' | 'video'>;
  source: Source;
  duration?: Frames;
  subtitle: string;
}>;

const updateSegmentPositionsStartingFrom = (segments: SegmentItem[], fromIndex: number = 0) => {
  for (let i = fromIndex; i < segments.length; i++) {
    const prevSegment = segments[i - 1];
    const framesCount = segments[i].end - segments[i].start;
    segments[i].start = prevSegment ? prevSegment.end : 0;
    segments[i].end = prevSegment ? prevSegment.end + framesCount : framesCount;
  }
};

export const clearAssemblyTemplate = createAsyncThunk<void, void, { state: RootState }>(
  'packagingTimeline/clearAssemblyTemplate',
  async (_, thunkAPI) => {
    thunkAPI.dispatch(setSegments([]));
    thunkAPI.dispatch(setOverlays([[]]));
    thunkAPI.dispatch(setActiveTemplate(null));
  }
);

const sliceName = 'packagingTimeline';

/**
 * This slice was split from the `packaging` slice because this part of the packaging state needs to store history.
 * This is achieved using the `redux-undo` library.
 *
 * Some of the reducers in this slice defined as a pair of `reducer` and `prepare` functions. We need that to allow passing the
 * metadata in the actions (TimelineActionMeta), to allow some action being excluded from the history.
 * A bit more information about passing meta into actions: https://stackoverflow.com/a/60322541/918181
 */
const packagingTimelineSlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    setSegments: (state, action: PayloadAction<SegmentItem[]>) => {
      state.segments = action.payload;
    },
    setOverlays: (state, action: PayloadAction<OverlayLayer[]>) => {
      state.overlays = action.payload;
    },

    appendSegment: {
      reducer(state, action: AppendSegmentPayloadAction) {
        const prevSegment = state.segments[state.segments.length - 1];
        const start = !prevSegment ? 0 : prevSegment.end;
        const end = start + identifyDefaultSegmentDuration(action.payload.kind);

        state.segments.push({
          ...action.payload,
          start,
          end
        });
      },
      prepare(payload: AppendSegmentPayload, meta?: TimelineActionMeta) {
        return { payload, meta };
      }
    },
    duplicateSegment: (state, { payload: segmentId }: PayloadAction<DraggableId>) => {
      const originalSegmentIndex = state.segments.findIndex((segment) => segment.draggableId === segmentId);
      if (originalSegmentIndex === -1) return;
      const segmentCopy = cloneDeep(state.segments[originalSegmentIndex]);
      segmentCopy.draggableId = nanoid(); // change `draggableId` because they should be unique
      state.segments.splice(originalSegmentIndex, 0, segmentCopy);
    },
    removeSegment: {
      reducer(state, action: PayloadAction<DraggableId>) {
        const removedSegmentIndex = state.segments.findIndex((segment) => segment.draggableId === action.payload);
        state.segments.splice(removedSegmentIndex, 1);
        updateSegmentPositionsStartingFrom(state.segments, removedSegmentIndex);
      },
      prepare(payload: DraggableId, meta?: TimelineActionMeta) {
        return { payload, meta };
      }
    },
    insertSegmentAtIndex: {
      reducer(state, { payload: { index, segment } }: InsertSegmentAtIndexPayloadAction) {
        state.segments.splice(index, 0, {
          ...segment,
          // at this point, we can set segment's `start` to 0 since right after the insertion we are going to recalculate the durations of inserted and all later segments
          start: 0,
          end: identifyDefaultSegmentDuration(segment.kind)
        });
        updateSegmentPositionsStartingFrom(state.segments, index);
      },
      prepare(payload: InsertSegmentAtIndexPayload, meta?: TimelineActionMeta) {
        return { payload, meta };
      }
    },
    reorderSegments: {
      reducer(state, { payload: { currentIndex, newIndex } }: MoveSegmentPayloadAction) {
        state.segments = arrayMove(state.segments, currentIndex, newIndex);
        updateSegmentPositionsStartingFrom(state.segments, Math.min(currentIndex, newIndex));
      },
      prepare(payload: MoveSegmentPayload, meta?: TimelineActionMeta) {
        return { payload, meta };
      }
    },
    commitSegmentChanges: (state, action: PayloadAction<DraggableId>) => {
      const segment = state.segments.find((segment) => segment.draggableId === action.payload);

      if (segment) {
        segment.isIntermediate = false;
      }
    },
    toggleSegmentLock: (state, action: PayloadAction<DraggableId>) => {
      const segment = state.segments.find((segment) => segment.draggableId === action.payload);

      if (segment) {
        segment.isLocked = !segment.isLocked;
      }
    },
    toggleOverlayLock: (state, action: PayloadAction<DraggableId>) => {
      const overlay = state.overlays.flat().find((overlay) => overlay.draggableId === action.payload);

      if (overlay) {
        overlay.isLocked = !overlay.isLocked;
      }
    },
    setSegmentSource: (state, { payload: { segmentId, source, duration, subtitle } }: SetSegmentSourcePayloadAction) => {
      const segmentIndex = state.segments.findIndex((segment) => segment.draggableId === segmentId);

      if (segmentIndex > -1) {
        state.segments[segmentIndex].source = source;
        state.segments[segmentIndex].start = 0;
        state.segments[segmentIndex].end = duration;
        state.segments[segmentIndex].subtitle = subtitle;
      }

      updateSegmentPositionsStartingFrom(state.segments, segmentIndex);
    },
    setPreviewUrls: {
      reducer(state, { payload: previewURLsBySegmentId }: SetSegmentPreviewUrlPayloadAction) {
        Object.entries(previewURLsBySegmentId).forEach(([timelineItemId, { previewUrl, previewLoadingError }]) => {
          // search for target segment
          const segment = state.segments.find((segment) => segment.draggableId === timelineItemId);
          // search for target overlay
          const overlay = state.overlays.flat().find((o) => o.draggableId === timelineItemId);

          const source = segment ? segment?.source : overlay && isAssignedOverlay(overlay) ? overlay.source : undefined;

          if (source) {
            if (previewUrl && previewLoadingError) {
              console.error(
                'Payload should not contain both `previewUrl` and `previewLoadingError` at the same time. This mean something is went wrong.'
              );
            }

            if (previewUrl) {
              source.previewUrl = previewUrl;
              source.previewLoadingError = undefined;
            } else if (previewLoadingError) {
              source.previewLoadingError = previewLoadingError;
              source.previewUrl = undefined;
            }
          }
        });
      },
      prepare(payload: SetSegmentPreviewUrlPayload, meta?: TimelineActionMeta) {
        return { payload, meta };
      }
    },
    resetAllPreviewUrls: {
      reducer(state) {
        [...state.segments, ...state.overlays.flat().filter(isAssignedOverlay)].forEach((timelineItem) => {
          const { source } = timelineItem;
          if (source) {
            source.previewUrl = undefined;
            source.previewLoadingError = undefined;
          }
        });
      },
      prepare(payload, meta?: TimelineActionMeta) {
        return { payload, meta };
      }
    },

    addOverlayLayer: (state) => {
      state.overlays.push([]);
    },
    removeOverlayLayer: (state, action: PayloadAction<number>) => {
      state.overlays.splice(action.payload, 1);
    },
    insertOverlay: (state, { payload: { layerIndex, overlay } }: InsertOverlayPayloadAction) => {
      state.overlays[layerIndex].push(overlay);
    },
    removeOverlay: (state, action: PayloadAction<{ layerIndex: number; overlayId: DraggableId }>) => {
      const { layerIndex, overlayId } = action.payload;
      state.overlays[layerIndex] = state.overlays[layerIndex].filter((overlay) => overlay.draggableId !== overlayId);
    },
    moveOverlay: (state, { payload: { overlayId, currentLayerIndex, newLayerIndex, start, end } }: MoveOverlayPayloadAction) => {
      const overlayIndex = state.overlays[currentLayerIndex].findIndex((item) => item.draggableId === overlayId);
      const overlay = state.overlays[currentLayerIndex][overlayIndex];
      overlay.start = start;
      overlay.end = end;
      if (currentLayerIndex !== newLayerIndex) {
        state.overlays[currentLayerIndex].splice(overlayIndex, 1);
        state.overlays[newLayerIndex].push(overlay);
      }
    },
    resizeOverlay: (state, { payload: { overlayId, layerIndex, end } }: ResizeOverlayPayloadAction) => {
      const overlay = state.overlays[layerIndex].find((item) => item.draggableId === overlayId);

      if (!overlay) {
        throw new Error('Overlay not found');
      }

      overlay.end = end;
    },
    setOverlaySource: (state, { payload }: SetOverlaySourcePayloadAction) => {
      const { overlayId, sourceType, duration, source, subtitle } = payload;
      let overlayIndex = -1;
      const layerIndex = state.overlays.findIndex((layer) =>
        layer.find((overlay, index) => {
          if (overlay.draggableId === overlayId) {
            overlayIndex = index;
            return true;
          }
        })
      );

      if (overlayIndex === -1) {
        throw new Error('Overlay not found');
      }

      if (sourceType === 'image' || sourceType === 'video') {
        const initial = state.overlays[layerIndex][overlayIndex];
        const updated: OverlayItemWithImage | OverlayItemWithVideo = {
          ...initial,
          sourceType,
          source,
          subtitle,
          ...(typeof duration !== 'undefined' ? { end: initial.start + duration } : {})
        };
        state.overlays[layerIndex][overlayIndex] = updated;
      } else {
        throw new Error(`"${sourceType}" sourceType is not supported`);
      }
    }
  }
});

export const {
  setSegments,
  setOverlays,

  appendSegment,
  duplicateSegment,
  removeSegment,
  insertSegmentAtIndex,
  reorderSegments,
  commitSegmentChanges,
  toggleSegmentLock,
  toggleOverlayLock,
  setSegmentSource,
  setPreviewUrls,
  resetAllPreviewUrls,

  addOverlayLayer,
  removeOverlayLayer,

  insertOverlay,
  removeOverlay,
  moveOverlay,
  resizeOverlay,
  setOverlaySource
} = packagingTimelineSlice.actions;

export default undoable(packagingTimelineSlice.reducer, {
  filter: (action) => {
    if (!action.type.startsWith(`${sliceName}/`)) {
      // For some reason when user cancels (by pressing ESC button) adding a new segment this `filter` function is called
      // with the "packaging/clearMovingOverlayData" action, which is a part of a different slice.
      // I don't know whether it's a lack of my understanding of how `redux-undo` library work or a bug inside the
      // library, but as a workaround I decided to filter out all actions whose name does not start with the
      // 'packagingTimeline/'.
      return false;
    }

    if ((action.meta as TimelineActionMeta | undefined)?.excludeFromHistory) {
      return false;
    }

    return true;
  }
});
